(() => {
  const storageKey = 'pushpaura:recently-viewed:v1';
  const maxProducts = 8;

  const readRecentlyViewed = () => {
    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey) || '[]');
      return Array.isArray(stored) ? stored.filter((handle) => typeof handle === 'string') : [];
    } catch {
      return [];
    }
  };

  const formatPrice = (price) => {
    const currency = window.Shopify?.currency?.active || 'INR';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
        style: 'currency', currency, maximumFractionDigits: 0,
      }).format(Number(price) / 100);
    } catch {
      return `${currency} ${Math.round(Number(price) / 100)}`;
    }
  };

  const createCard = (product, cartAddUrl, quickBuyLabel) => {
    const variant = product.variants?.find((item) => item.available);
    if (!variant) return null;

    const card = document.createElement('div');
    card.className = 'collection-card group';
    const media = document.createElement('div');
    media.className = 'collection-card__media';
    const link = document.createElement('a');
    link.href = product.url;
    link.setAttribute('aria-label', product.title);

    if (product.featured_image) {
      const image = document.createElement('img');
      image.src = product.featured_image;
      image.alt = product.title;
      image.width = 800;
      image.height = 1000;
      image.loading = 'lazy';
      link.append(image);
    }
    media.append(link);

    if (product.compare_at_price > product.price) {
      const badge = document.createElement('span');
      badge.className = 'collection-card__badge';
      badge.textContent = 'ON SALE';
      media.append(badge);
    }

    const productForm = document.createElement('div');
    productForm.className = 'collection-card__quick-add';
    const form = document.createElement('form');
    form.className = 'form';
    form.method = 'post';
    form.action = cartAddUrl;
    form.noValidate = true;
    form.dataset.recentQuickAdd = 'true';
    const variantInput = document.createElement('input');
    variantInput.type = 'hidden';
    variantInput.name = 'id';
    variantInput.value = variant.id;
    variantInput.className = 'product-variant-id';
    form.append(variantInput);

    const quickBuy = document.createElement('button');
    quickBuy.type = 'submit';
    quickBuy.name = 'add';
    quickBuy.className = 'collection-card__quick-add-btn';
    quickBuy.setAttribute('aria-label', `${quickBuyLabel} — ${product.title}`);
    const iconWrap = document.createElement('span');
    const icon = document.createElement('i');
    icon.className = 'fa-solid fa-bag-shopping';
    icon.setAttribute('aria-hidden', 'true');
    iconWrap.append(icon);
    const spinner = document.createElement('span');
    spinner.className = 'loading__spinner hidden';
    quickBuy.append(iconWrap, spinner);
    form.append(quickBuy);
    productForm.append(form);
    media.append(productForm);

    const title = document.createElement('h3');
    title.className = 'collection-card__title';
    const titleLink = document.createElement('a');
    titleLink.href = product.url;
    titleLink.textContent = product.title;
    title.append(titleLink);

    const price = document.createElement('div');
    price.className = 'collection-card__price';
    price.textContent = product.price_varies ? `From ${formatPrice(product.price_min)}` : formatPrice(variant.price);
    if (product.compare_at_price > product.price) {
      const compare = document.createElement('s');
      compare.className = 'collection-card__compare';
      compare.textContent = formatPrice(product.compare_at_price);
      price.append(compare);
    }

    const colorOption = product.options?.find((option) => /colou?r/i.test(option.name));
    card.append(media, title, price);
    if (colorOption?.values?.length) {
      const colors = document.createElement('p');
      colors.className = 'collection-card__colors';
      colors.textContent = `${colorOption.values.length} ${colorOption.values.length === 1 ? 'color' : 'colors'}`;
      card.append(colors);
    }
    return card;
  };

  const initialize = (root) => {
    const currentHandle = root.dataset.productHandle;
    const cartAddUrl = root.dataset.cartAddUrl || '/cart/add';
    const recentTrack = root.querySelector('[data-recent-products]');
    const emptyMessage = root.querySelector('[data-recent-empty]');
    const tabs = [...root.querySelectorAll('[data-discovery-tab]')];
    const panels = [...root.querySelectorAll('[data-discovery-panel]')];
    const quickBuyLabel = root.dataset.quickBuyLabel || 'Quick buy';
    if (!currentHandle || !recentTrack) return;

    tabs.forEach((tab) => tab.addEventListener('click', () => {
      const selectedName = tab.dataset.discoveryTab;
      tabs.forEach((candidate) => {
        const selected = candidate === tab;
        candidate.setAttribute('aria-selected', String(selected));
        candidate.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => { panel.hidden = panel.dataset.discoveryPanel !== selectedName; });
    }));

    root.addEventListener('submit', async (event) => {
      const form = event.target.closest('form[data-recent-quick-add]');
      if (!form) return;
      event.preventDefault();

      const button = form.querySelector('[type="submit"]');
      const spinner = button?.querySelector('.loading__spinner');
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      if (!button) return;
      button.disabled = true;
      button.classList.add('loading');
      spinner?.classList.remove('hidden');

      try {
        const formData = new FormData(form);
        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        const response = await fetch(form.action, {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
          body: formData,
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || 'Could not add this item to your cart.');
        if (cart) cart.renderContents(result);
        else window.location.assign('/cart');
      } catch (error) {
        button.title = error.message;
        console.error(error);
      } finally {
        button.disabled = false;
        button.classList.remove('loading');
        spinner?.classList.add('hidden');
      }
    });

    const handles = readRecentlyViewed().filter((handle) => handle !== currentHandle).slice(0, maxProducts);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([currentHandle, ...handles].slice(0, maxProducts)));
    } catch {
      // Keep recommendations usable when browser storage is unavailable.
    }

    if (!handles.length) {
      if (emptyMessage) emptyMessage.hidden = false;
      return;
    }

    Promise.all(handles.map((handle) => fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((response) => response.ok ? response.json() : null)
      .catch(() => null)))
      .then((products) => {
        products.filter(Boolean).forEach((product) => {
          product.url = product.url || `/products/${encodeURIComponent(product.handle)}`;
          const card = createCard(product, cartAddUrl, quickBuyLabel);
          if (!card) return;
          const item = document.createElement('li');
          item.className = 'grid__item';
          item.append(card);
          recentTrack.append(item);
        });
        if (!recentTrack.children.length && emptyMessage) emptyMessage.hidden = false;
      });
  };

  const start = () => document.querySelectorAll('[data-product-discovery]').forEach(initialize);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
